import React, { useEffect, useRef, useState } from "react";
import { CloudUpload, X, Video, CheckCircle2 } from "lucide-react";

import { FormLayout } from "../../../../../components/layout/forms/FormLayout";
import FormHeader from "../../../../../components/layout/forms/header/FormHeader";
import Heading from "../../../../../components/typography/Heading";
import Text from "../../../../../components/typography/Text";
import Button from "../../../../../components/buttons/Button";

import TextField from "../../../../../components/input/textfield/TextField";
import TextArea from "../../../../../components/input/textarea/TextArea";
import Select from "../../../../../components/input/select/Select";

import { useCreatorVideoUpload } from "../uploads/hooks/useCreatorVideoUpload";

import { generateUniqueId } from "../../../../../utils/helpers/generator/generateUniqueId";
import { s3Uploader } from "../../../../../utils/helpers/s3Uploader/s3Uploader";

import {
  getSetPriceAmount,
  validateVideoPriceAmount,
} from "./utils/helper/videoPricing";

import "./CreatorVideoUploadForm.css";

const VIDEO_VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Unlisted", value: "unlisted" },
  { label: "Group", value: "group" },
  { label: "Private", value: "private" },
];

const SPORT_OPTIONS = [
  { label: "Rugby Union", value: "Rugby Union" },
  { label: "Rugby League", value: "Rugby League" },
  { label: "Other", value: "Other" },
];

const MAX_VIDEO_SIZE = 7 * 1024 * 1024 * 1024;
const MIN_VIDEO_DURATION = 60;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const formatDuration = (seconds) => {
  const total = Math.floor(Number(seconds) || 0);
  const minutes = Math.floor(total / 60);
  const remaining = total % 60;

  return `${minutes}m ${String(remaining).padStart(2, "0")}s`;
};

const formatFileSize = (bytes) => {
  const size = Number(bytes) || 0;

  return size >= 1024 * 1024 * 1024
    ? `${(size / 1024 ** 3).toFixed(2)} GB`
    : `${(size / 1024 ** 2).toFixed(2)} MB`;
};

export default function CreatorVideoUploadForm({
  username,
  onClose,
  onUploadingChange,
  onSuccess,
}) {
  const {
    loading,
    error,
    setError,
    uploadingVideo,
    uploadingThumbnail,
    addCreatorVideo,
    getCreatorVideoUploadUrl,
    getCreatorVideoThumbnailUploadUrl,
  } = useCreatorVideoUpload(username);

  const [videoFile, setVideoFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [fileProcessing, setFileProcessing] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [priceTouched, setPriceTouched] = useState(false);

  const [formData, setFormData] = useState({
    videoTag: generateUniqueId(6),
    title: "",
    sport: "",
    description: "",
    visibility: "private",
    priceAmount: "",
  });

  const videoPreviewRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const videoUrlRef = useRef(null);
  const fileSelectionIdRef = useRef(0);
  const uploadInProgressRef = useRef(false);
  const mountedRef = useRef(true);

  const uploading =
    isUploading || loading || uploadingVideo || uploadingThumbnail;

  const minimumPrice = getSetPriceAmount(videoFile?.duration);

  const priceValidation = validateVideoPriceAmount(
    formData.priceAmount,
    videoFile?.duration,
  );

  const priceError =
    priceTouched && videoFile && !priceValidation.valid
      ? priceValidation.message
      : "";

  const isBusy = uploading || fileProcessing;
  const canSubmit =
    Boolean(username) && Boolean(videoFile) && !isBusy && priceValidation.valid;

  const displayError =
    uploadError || (typeof error === "string" ? error : error?.message || "");

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      fileSelectionIdRef.current += 1;

      if (videoUrlRef.current) {
        URL.revokeObjectURL(videoUrlRef.current);
        videoUrlRef.current = null;
      }
    };
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handlePriceChange = (event) => {
    const value = typeof event === "string" ? event : event.target.value;

    if (value !== "" && !/^\d*(?:\.\d{0,2})?$/.test(value)) {
      return;
    }

    setFormData((previous) => ({
      ...previous,
      priceAmount: value,
    }));

    setPriceTouched(true);
    setUploadError("");
  };

  const processFile = async (file) => {
    if (!file || uploading || fileProcessing) return;

    setUploadError("");
    setError("");

    if (file.type !== "video/mp4") {
      setUploadError("Only MP4 video files are allowed.");
      return;
    }

    if (file.size > MAX_VIDEO_SIZE) {
      setUploadError("File size exceeds the 7GB limit.");
      return;
    }

    if (file.size <= 0) {
      setUploadError("The selected video file is empty.");
      return;
    }

    const selectionId = ++fileSelectionIdRef.current;

    setFileProcessing(true);

    const metadataUrl = URL.createObjectURL(file);
    const videoNode = document.createElement("video");

    videoNode.preload = "metadata";
    videoNode.muted = true;
    videoNode.playsInline = true;

    try {
      const duration = await new Promise((resolve, reject) => {
        videoNode.onloadedmetadata = () => {
          const seconds = Number(videoNode.duration);

          if (!Number.isFinite(seconds) || seconds <= 0) {
            reject(new Error("Unable to read video duration."));
            return;
          }

          resolve(seconds);
        };

        videoNode.onerror = () => {
          reject(new Error("The selected video could not be read."));
        };

        videoNode.src = metadataUrl;
      });

      if (!mountedRef.current || selectionId !== fileSelectionIdRef.current) {
        return;
      }

      if (duration < MIN_VIDEO_DURATION) {
        setUploadError("Videos must be at least 60 seconds long.");
        return;
      }

      const calculatedPrice = getSetPriceAmount(duration);

      if (calculatedPrice === null) {
        setUploadError("Unable to calculate the minimum video price.");
        return;
      }

      const randomId = generateUniqueId();
      const previewUrl = URL.createObjectURL(file);

      if (videoUrlRef.current) {
        URL.revokeObjectURL(videoUrlRef.current);
      }

      videoUrlRef.current = previewUrl;

      setVideoFile({
        file,
        type: file.type,
        size: file.size,
        duration,
        filename: `${randomId}.mp4`,
        url: previewUrl,
      });

      setFormData((previous) => ({
        ...previous,
        title: previous.title.trim() || file.name.replace(/\.[^.]+$/, ""),
        priceAmount: calculatedPrice.toFixed(2),
      }));

      setPriceTouched(false);
      setProgress(0);
    } catch (requestError) {
      if (mountedRef.current && selectionId === fileSelectionIdRef.current) {
        setUploadError(
          getErrorMessage(
            requestError,
            "Unable to process the selected video.",
          ),
        );
      }
    } finally {
      videoNode.removeAttribute("src");
      videoNode.load();

      URL.revokeObjectURL(metadataUrl);

      if (mountedRef.current && selectionId === fileSelectionIdRef.current) {
        setFileProcessing(false);
      }
    }
  };

  const removeVideoFile = () => {
    if (isBusy) return;

    fileSelectionIdRef.current += 1;

    if (videoUrlRef.current) {
      URL.revokeObjectURL(videoUrlRef.current);
      videoUrlRef.current = null;
    }

    setVideoFile(null);
    setProgress(0);
    setUploadError("");
    setFileProcessing(false);
    setPriceTouched(false);

    setFormData((previous) => ({
      ...previous,
      priceAmount: "",
    }));

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const captureThumbnail = () => {
    return new Promise((resolve, reject) => {
      const video = videoPreviewRef.current;
      const canvas = canvasRef.current;

      if (!video || !canvas) {
        reject(new Error("Video preview is not ready."));
        return;
      }

      const context = canvas.getContext("2d");

      if (!context) {
        reject(new Error("Unable to create the video thumbnail."));
        return;
      }

      const createThumbnail = () => {
        if (!video.videoWidth || !video.videoHeight) {
          reject(new Error("Video dimensions are unavailable."));
          return;
        }

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        try {
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
        } catch (requestError) {
          reject(requestError);
          return;
        }

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Thumbnail generation failed."));
              return;
            }

            const thumbnailKey = `${generateUniqueId()}.jpg`;

            resolve(
              new File([blob], thumbnailKey, {
                type: "image/jpeg",
              }),
            );
          },
          "image/jpeg",
          0.7,
        );
      };

      if (video.readyState < 1) {
        reject(new Error("Video metadata has not loaded."));
        return;
      }

      const thumbnailTime = Math.min(2, Math.max(video.duration - 0.1, 0));

      if (
        video.readyState >= 2 &&
        Math.abs(video.currentTime - thumbnailTime) < 0.05
      ) {
        createThumbnail();
        return;
      }

      let settled = false;

      const cleanup = () => {
        video.removeEventListener("seeked", handleSeeked);
        video.removeEventListener("error", handleError);
      };

      const handleSeeked = () => {
        if (settled) return;

        settled = true;
        cleanup();
        createThumbnail();
      };

      const handleError = () => {
        if (settled) return;

        settled = true;
        cleanup();
        reject(new Error("Unable to capture video thumbnail."));
      };

      video.addEventListener("seeked", handleSeeked);
      video.addEventListener("error", handleError);

      try {
        video.currentTime = thumbnailTime;
      } catch (requestError) {
        cleanup();
        reject(requestError);
      }
    });
  };

  const validateUploadReady = () => {
    if (!username) {
      setUploadError("Creator username is required.");
      return false;
    }

    const requiredFields = ["title", "sport", "visibility"];

    const formValid = requiredFields.every(
      (field) => String(formData[field] || "").trim() !== "",
    );

    if (!formValid) {
      setUploadError("Please fill in all required fields.");
      return false;
    }

    const videoValid = Boolean(
      videoFile?.file &&
      videoFile?.filename &&
      videoFile?.duration &&
      videoFile?.size,
    );

    if (!videoValid) {
      setUploadError(
        "Video processing is incomplete. Please re-select the file.",
      );
      return false;
    }

    const validation = validateVideoPriceAmount(
      formData.priceAmount,
      videoFile.duration,
    );

    if (!validation.valid) {
      setPriceTouched(true);
      setUploadError(validation.message);
      return false;
    }

    return true;
  };

  const startUpload = async () => {
    if (
      !username ||
      !videoFile ||
      fileProcessing ||
      uploadInProgressRef.current ||
      uploading
    ) {
      return;
    }

    uploadInProgressRef.current = true;

    setUploadError("");
    setError("");
    setProgress(0);

    let succeeded = false;

    try {
      if (!validateUploadReady()) return;

      setIsUploading(true);
      onUploadingChange?.(true);

      const thumbnailFile = await captureThumbnail();

      setProgress(5);

      const [videoUploadData, thumbnailUploadData] = await Promise.all([
        getCreatorVideoUploadUrl(),
        getCreatorVideoThumbnailUploadUrl(),
      ]);

      if (!videoUploadData || !thumbnailUploadData) {
        throw new Error("Unable to prepare video or thumbnail upload.");
      }

      await s3Uploader(
        videoUploadData,
        {
          file: videoFile.file,
          filename: videoFile.filename,
        },
        0,
        (_index, percent) => {
          const safePercent = Math.min(100, Math.max(0, Number(percent) || 0));

          if (mountedRef.current) {
            setProgress(Math.round(5 + safePercent * 0.85));
          }
        },
      );

      if (mountedRef.current) setProgress(90);

      await s3Uploader(thumbnailUploadData, {
        file: thumbnailFile,
        filename: thumbnailFile.name,
      });

      if (mountedRef.current) setProgress(95);

      const finalPrice = validateVideoPriceAmount(
        formData.priceAmount,
        videoFile.duration,
      );

      if (!finalPrice.valid) {
        throw new Error(finalPrice.message);
      }

      const payload = {
        videoTag: formData.videoTag,
        title: formData.title.trim(),
        sport: formData.sport,
        description: formData.description?.trim() || "",
        visibility: formData.visibility.toLowerCase(),
        priceAmount: finalPrice.priceAmount,
        thumbnailKey: thumbnailFile.name,
        videoKey: videoFile.filename,
        videoSize: videoFile.size,
        videoType: videoFile.type,
        videoDuration: videoFile.duration,
      };

      const result = await addCreatorVideo(payload);

      if (!result || result.success !== true) {
        throw new Error(
          result?.message || "Unable to save the uploaded video.",
        );
      }

      succeeded = true;

      if (mountedRef.current) setProgress(100);

      onSuccess?.(result);
      onClose?.();
    } catch (requestError) {
      console.error("Creator video upload failed:", requestError);

      if (mountedRef.current) {
        setUploadError(
          getErrorMessage(requestError, "An error occurred during upload."),
        );
      }
    } finally {
      uploadInProgressRef.current = false;

      if (mountedRef.current) {
        setIsUploading(false);
      }

      if (!succeeded || mountedRef.current) {
        onUploadingChange?.(false);
      }
    }
  };

  const handleSubmit = (event) => {
    event?.preventDefault();
    void startUpload();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    if (isBusy) return;

    void processFile(event.dataTransfer?.files?.[0]);
  };

  const progressLabel =
    progress < 90
      ? "Uploading video"
      : progress < 95
        ? "Uploading thumbnail"
        : progress < 100
          ? "Saving video"
          : "Upload complete";

  return (
    <FormLayout>
      <FormHeader
        title="Upload Video"
        description="Upload an MP4 video, up to 7GB.."
      />

      <div className="creator-video-upload-form__body">
        {displayError && (
          <div className="creator-video-upload-form__error" role="alert">
            <Text as="p" size="sm">
              {displayError}
            </Text>
          </div>
        )}

        <section className="creator-video-upload-form__section">
          {!videoFile ? (
            <div
              className={`creator-video-upload-form__dropzone ${
                isDragging ? "creator-video-upload-form__dropzone--active" : ""
              }`}
              role="button"
              tabIndex={isBusy ? -1 : 0}
              aria-disabled={isBusy}
              onDragOver={(event) => {
                event.preventDefault();

                if (!isBusy) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => {
                if (!isBusy) fileInputRef.current?.click();
              }}
              onKeyDown={(event) => {
                if ((event.key === "Enter" || event.key === " ") && !isBusy) {
                  event.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept="video/mp4,.mp4"
                disabled={isBusy}
                onChange={(event) => {
                  void processFile(event.target.files?.[0]);
                }}
              />

              <div className="creator-video-upload-form__upload-icon">
                <CloudUpload size={28} />
              </div>

              <Heading as="h4" size="sm">
                {fileProcessing ? "Processing video..." : "Choose an MP4 video"}
              </Heading>

              <Text as="p" tone="secondary" size="sm">
                {fileProcessing
                  ? "Reading video metadata"
                  : "Click to browse or drag and drop"}
              </Text>
            </div>
          ) : (
            <div className="creator-video-upload-form__preview">
              <div className="creator-video-upload-form__player">
                <video
                  ref={videoPreviewRef}
                  src={videoFile.url}
                  controls
                  playsInline
                  preload="auto"
                  controlsList="nodownload"
                  disablePictureInPicture
                  onContextMenu={(event) => event.preventDefault()}
                />

                <canvas ref={canvasRef} hidden />

                {!isBusy && (
                  <button
                    type="button"
                    className="creator-video-upload-form__remove"
                    onClick={removeVideoFile}
                    aria-label="Remove selected video"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>

              <div className="creator-video-upload-form__file-meta">
                <div className="creator-video-upload-form__file-icon">
                  <Video size={18} />
                </div>

                <div className="creator-video-upload-form__file-info">
                  <Text as="span" size="sm" weight="medium">
                    {videoFile.file.name}
                  </Text>

                  <Text as="span" size="xs" tone="secondary">
                    {formatDuration(videoFile.duration)} ·{" "}
                    {formatFileSize(videoFile.size)}
                  </Text>
                </div>

                <CheckCircle2
                  size={18}
                  className="creator-video-upload-form__file-success"
                />
              </div>
            </div>
          )}
        </section>

        <section className="creator-video-upload-form__section">
          <div className="creator-video-upload-form__section-heading">
            <Heading as="h3" size="sm">
              Video Details
            </Heading>

            <Text as="p" size="sm" tone="secondary">
              Provide information about your video.
            </Text>
          </div>

          <div className="creator-video-upload-form__fields">
            <TextField
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              disabled={isBusy}
              placeholder="Match name or highlight title"
            />

            <div className="creator-video-upload-form__row">
              <Select
                label="Sport"
                name="sport"
                defaultOption="Select sport"
                value={formData.sport}
                onChange={handleInputChange}
                items={SPORT_OPTIONS}
                required
                disabled={isBusy}
              />

              <Select
                label="Visibility"
                name="visibility"
                value={formData.visibility}
                onChange={handleInputChange}
                items={VIDEO_VISIBILITY_OPTIONS}
                required
                disabled={isBusy}
              />
            </div>

            <TextArea
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              maxLength={500}
              disabled={isBusy}
              placeholder="Add context for viewers, players or scouts"
            />
          </div>
        </section>

        <section className="creator-video-upload-form__section">
          <div className="creator-video-upload-form__section-heading">
            <Heading as="h3" size="sm">
              Rental Pricing
            </Heading>

            <Text as="p" size="sm" tone="secondary">
              Set the rental price for your video.
            </Text>
          </div>

          <div className="creator-video-upload-form__pricing">
            <TextField
              label="Rental Price (AUD)"
              name="priceAmount"
              type="number"
              min={minimumPrice ?? undefined}
              step="0.01"
              inputMode="decimal"
              value={formData.priceAmount}
              onChange={handlePriceChange}
              onBlur={() => setPriceTouched(true)}
              required
              disabled={!videoFile || isBusy}
              placeholder={
                minimumPrice === null
                  ? "Select a video"
                  : `Minimum A$${minimumPrice.toFixed(2)}`
              }
            />

            <Text as="p" size="xs" tone="secondary">
              {minimumPrice === null
                ? "Choose a video to calculate its minimum rental price."
                : `Minimum rental price: A$${minimumPrice.toFixed(2)}. You can set a higher price.`}
            </Text>

            {priceError && (
              <Text
                as="p"
                size="xs"
                className="creator-video-upload-form__price-error"
              >
                {priceError}
              </Text>
            )}
          </div>
        </section>

        {uploading && (
          <div
            className="creator-video-upload-form__progress"
            role="status"
            aria-live="polite"
          >
            <div className="creator-video-upload-form__progress-heading">
              <Text as="span" size="sm">
                {progressLabel}
              </Text>

              <Text as="strong" size="sm" weight="semibold">
                {progress}%
              </Text>
            </div>

            <div
              className="creator-video-upload-form__progress-track"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Video upload progress"
            >
              <div
                className="creator-video-upload-form__progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: "1rem", marginTop: "1.5rem" }}>
        <Button
          value="Cancel"
          variant="dark-outlined"
          onClick={() => onClose?.()}
          disabled={isBusy}
        />

        <Button
          value={uploading ? `Uploading ${progress}%` : "Publish Video"}
          variant="primary"
          onClick={startUpload}
          disabled={!canSubmit}
        />
      </div>
    </FormLayout>
  );
}
